import { Building2, ShieldCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  ORGANISATION_ROLE_LABELS,
  type OrganisationAssignment,
  type OrganisationRole,
} from '@/contexts/OrganisationContext';

const ROLE_STYLES: Record<OrganisationRole, string> = {
  company_admin: 'border-purple-200 bg-purple-50 text-purple-800',
  branch_admin: 'border-blue-200 bg-blue-50 text-blue-800',
  unit_admin: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  unit_manager: 'border-cyan-200 bg-cyan-50 text-cyan-800',
  user: 'border-slate-200 bg-slate-50 text-slate-700',
};

function formatOrganisationScope(assignment: OrganisationAssignment): string {
  if (assignment.branch_name && assignment.unit_name) {
    return `${assignment.branch_name} → ${assignment.unit_name}`;
  }
  return assignment.branch_name || assignment.unit_name || 'Company-wide';
}

interface OrganisationAccessSummaryProps {
  assignments?: readonly OrganisationAssignment[];
  compact?: boolean;
}

export function OrganisationAccessSummary({
  assignments,
  compact = false,
}: OrganisationAccessSummaryProps) {
  if (!assignments) {
    return <span className="text-sm font-medium text-destructive">Organisation access unavailable</span>;
  }

  const activeAssignments = assignments.filter((assignment) => assignment.status === 'active');

  if (!activeAssignments.length) {
    return <span className="text-sm text-muted-foreground">No scoped role assigned</span>;
  }

  if (compact) {
    const primary = activeAssignments.find((assignment) => assignment.is_primary) || activeAssignments[0];
    const additionalCount = activeAssignments.length - 1;

    return (
      <div className="min-w-[210px] space-y-1.5">
        <Badge variant="outline" className={ROLE_STYLES[primary.role]}>
          <ShieldCheck className="mr-1 h-3.5 w-3.5" />
          {ORGANISATION_ROLE_LABELS[primary.role]}
        </Badge>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Building2 className="h-3.5 w-3.5 shrink-0" />
          <span>{formatOrganisationScope(primary)}</span>
          {primary.is_primary ? <span>· Primary</span> : null}
        </div>
        {additionalCount > 0 ? (
          <div className="text-xs font-medium text-primary">
            +{additionalCount} additional role{additionalCount === 1 ? '' : 's'}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {activeAssignments.map((assignment) => (
        <div
          key={assignment.id}
          className="flex flex-col gap-2 rounded-lg border bg-muted/20 p-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="min-w-0">
            <Badge variant="outline" className={ROLE_STYLES[assignment.role]}>
              <ShieldCheck className="mr-1 h-3.5 w-3.5" />
              {ORGANISATION_ROLE_LABELS[assignment.role]}
            </Badge>
            <div className="mt-1.5 flex items-center gap-1.5 text-sm text-muted-foreground">
              <Building2 className="h-4 w-4 shrink-0" />
              <span>{formatOrganisationScope(assignment)}</span>
            </div>
          </div>
          {assignment.is_primary ? (
            <Badge variant="secondary" className="w-fit">Primary context</Badge>
          ) : (
            <span className="text-xs text-muted-foreground">Additional access</span>
          )}
        </div>
      ))}
    </div>
  );
}
